
"use client";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle, Target } from "lucide-react";
import HelpTooltip from "@/components/ui/HelpTooltip";

interface SafetyZoneIndicatorProps {
    passRate: number; // Percentage (0-100)
}

export default function SafetyZoneIndicator({ passRate }: SafetyZoneIndicatorProps) {
    // Comenius cutoff is ~55%. We'll set 60% as "Safe".
    const CUTOFF = 55;
    const SAFE_ZONE = 60;

    let statusColor = "text-red-500";
    let statusText = "At Risk";
    let Icon = AlertTriangle;

    if (passRate >= SAFE_ZONE) {
        statusColor = "text-green-500";
        statusText = "Safe Zone";
        Icon = CheckCircle;
    } else if (passRate >= CUTOFF) {
        statusColor = "text-yellow-500";
        statusText = "Borderline";
        Icon = Target;
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-lg flex justify-between items-center">
                    <div className="flex items-center">
                        Approving Probability
                        <HelpTooltip content={
                            <div className="space-y-2">
                                <p>This metric estimates your chance of passing the real exam based on your last 10 simulations.</p>
                                <ul className="list-disc pl-4 space-y-1 text-xs text-muted-foreground">
                                    <li><span className="text-green-600 font-bold">Safe Zone ({'>'}60%)</span>: You are consistently performing above the cutoff.</li>
                                    <li><span className="text-yellow-600 font-bold">Borderline (55-60%)</span>: You are passing, but close to the limit.</li>
                                    <li><span className="text-red-500 font-bold">At Risk ({'<'}55%)</span>: You need to improve your score to reach the 55% passing grade.</li>
                                </ul>
                            </div>
                        } />
                    </div>
                    <Badge variant={passRate >= CUTOFF ? "default" : "destructive"}>
                        Last 10 Exams
                    </Badge>
                </CardTitle>
                <CardDescription>Target: Maintain above {SAFE_ZONE}% for safety.</CardDescription>
            </CardHeader>
            <CardContent>
                <div className="flex flex-col items-center justify-center space-y-4">
                    <div className="relative w-full h-4 bg-secondary rounded-full overflow-hidden">
                        {/* Threshold Marker */}
                        <div
                            className="absolute top-0 bottom-0 w-1 bg-black/20 z-10"
                            style={{ left: `${CUTOFF}%` }}
                            title={`Official Cutoff: ${CUTOFF}%`}
                        />
                        <div
                            className="h-full bg-primary transition-all duration-500 ease-in-out"
                            style={{ width: `${passRate}%`, backgroundColor: passRate >= CUTOFF ? 'hsl(var(--primary))' : 'hsl(var(--destructive))' }}
                        />
                    </div>

                    <div className="flex justify-between w-full text-xs text-muted-foreground">
                        <span>0%</span>
                        <span className="font-bold text-foreground">{Math.round(passRate)}%</span>
                        <span>100%</span>
                    </div>

                    <div className={`flex items-center space-x-2 text-xl font-bold ${statusColor}`}>
                        <Icon className="w-6 h-6" />
                        <span>{statusText}</span>
                    </div>

                    <p className="text-center text-sm text-muted-foreground">
                        You have passed <strong>{Math.round(passRate)}%</strong> of your recent simulations.
                        The official cutoff is usually around 55%.
                    </p>
                </div>
            </CardContent>
        </Card>
    );
}
